import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { pageKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import { useAdminGhosting } from '@/dashboard/context/AdminGhostingContext/AdminGhostingContext';
import {
  updatePage as updatePageApi,
  attachPageFile,
  detachPageFile,
  attachPageImage,
  detachPageImage,
} from './queries/usePageQueries';
import useResolvedPageTheme from './useResolvedPageTheme';
import { saveNavSnapshot } from '@dashboard/lib/pageNavSnapshot';

const PageContext = React.createContext();

const getPageSellerId = page => page?.seller?._id ?? page?.seller;

export const PageProvider = ({ children, pageId, initialData }) => {
  const {
    data: rawData = {},
    isLoading,
    isError,
    isRefetching,
    refetch,
  } = usePageQuery(pageId, initialData);

  const data = useResolvedPageTheme(rawData);

  // react-query v5 reports isLoading false for a query that is disabled or has
  // otherwise produced no data. Consumers gate the page-edit UI on isLoading,
  // so treat "no page yet" as still loading to keep them from rendering `{}`
  // (Sentry CHECKOUT-PAGE-FRONTEND-6NN).
  const hasPage = !!rawData?._id;

  const { updatePage, attachFile, detachFile, attachImage, detachImage } = usePageMutation(
    pageId,
    data,
  );

  const { setAdminGhostingSellerId } = useAdminGhosting();

  React.useEffect(() => {
    if (rawData?._id) saveNavSnapshot(rawData);
  }, [rawData]);

  React.useEffect(() => {
    if (data?.seller) {
      setAdminGhostingSellerId(getPageSellerId(data));
    }
  }, [data?.seller, setAdminGhostingSellerId]);

  // Keep this below every hook: an early return above a hook drops the hook
  // count on the render where isError flips and React throws error #300 into
  // the app error boundary instead of showing this screen.
  if (isError) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-lg font-semibold mb-2">Something went wrong</h1>

          <p className="text-gray-500 dark:text-muted-foreground">
            Please refresh the page and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageContext.Provider
      value={{
        page: data,
        isLoading: isLoading || !hasPage,
        isError,
        isRefetching,
        refetch,
        updatePage,
        attachFile,
        detachFile,
        attachImage,
        detachImage,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

function usePageQuery(pageId, initialData) {
  const { token, sellerId } = useAuth();

  const { isLoading, isError, data, isRefetching, refetch } = useQuery({
    queryKey: pageKey(pageId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/${pageId}`, token),
    enabled: !!pageId && !!token,
    initialData,
  });

  return {
    isRefetching,
    isLoading,
    isError,
    data,
    refetch,
  };
}

function usePageMutation(pageId) {
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const updatePage = useMutation({
    mutationFn: values => updatePageApi(sellerId, token, values, pageId, queryClient),

    // Optimistic update
    onMutate: async values => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: pageKey(pageId) });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(pageKey(pageId));

      // Create a timestamp for this update to track the most recent update
      const updateTimestamp = Date.now();

      // Optimistically update to the new value
      queryClient.setQueryData(pageKey(pageId), old => {
        // Don't update if this is older than a previous update
        if (old?._lastUpdateTimestamp && old._lastUpdateTimestamp > updateTimestamp) {
          return old;
        }

        return {
          ...old,
          ...values,
          _lastUpdateTimestamp: updateTimestamp,
        };
      });

      // Return a context object with the previous data and timestamp
      return {
        previousData,
        updateTimestamp,
      };
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (error, values, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(pageKey(pageId), context.previousData);
      }
    },

    // Use the mutation response directly instead of refetching
    onSettled: (data, error, variables, context) => {
      if (!error && data?.data && !data.error) {
        const currentData = queryClient.getQueryData(pageKey(pageId));

        if (
          !currentData?._lastUpdateTimestamp ||
          context.updateTimestamp >= currentData._lastUpdateTimestamp
        ) {
          // Set the server response directly — avoids a redundant refetch
          // that would cause a second full re-render cascade
          queryClient.setQueryData(pageKey(pageId), {
            ...data.data,
            _lastUpdateTimestamp: context.updateTimestamp,
          });
        }
      }
    },
  });

  // fetcher already unwraps the `{ data }` envelope, so the page is the response.
  function setPageFilesFromResponse(updated) {
    if (!updated?.files) return;

    queryClient.setQueryData(pageKey(pageId), old =>
      old ? { ...old, files: updated.files } : old,
    );
  }

  const attachFile = useMutation({
    mutationFn: fileId => attachPageFile(sellerId, token, pageId, fileId),
    onSuccess: setPageFilesFromResponse,
  });

  const detachFile = useMutation({
    mutationFn: fileId => detachPageFile(sellerId, token, pageId, fileId),
    onSuccess: setPageFilesFromResponse,
  });

  function setPageImagesFromResponse(updated) {
    if (!updated?.images) return;

    queryClient.setQueryData(pageKey(pageId), old =>
      old ? { ...old, images: updated.images } : old,
    );
  }

  const attachImage = useMutation({
    mutationFn: fileId => attachPageImage(sellerId, token, pageId, fileId),
    onSuccess: setPageImagesFromResponse,
  });

  const detachImage = useMutation({
    mutationFn: fileId => detachPageImage(sellerId, token, pageId, fileId),
    onSuccess: setPageImagesFromResponse,
  });

  return {
    updatePage,
    attachFile,
    detachFile,
    attachImage,
    detachImage,
  };
}

export default function usePage() {
  const context = React.useContext(PageContext);

  if (context === undefined) {
    throw new Error('usePage must be used within a PageProvider');
  }

  return context;
}

// An admin ghosting a store is signed in as their own seller, so requests for
// the page's records need the seller that owns it. Outside a page: the signed-in one.
export function usePageSellerId() {
  const page = React.useContext(PageContext)?.page;
  const { sellerId } = useAuth();

  return getPageSellerId(page) ?? sellerId;
}
